import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { siteConfig } from '../../utils/seo-config';

const SEO = ({
  title,
  description,
  keywords,
  image,
  twitterImage,
  linkedinImage,
  facebookImage,
  url,
  type = 'website',
  article = null,
  structuredData = null,
  canonicalUrl = null,
  noindex = false,
  locale: localeProp = null,
  alternateUrls = [],
  languageAlternates = null,
  geoPosition = { latitude: 19.4326, longitude: -99.1332 },
  businessHours = null
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.language;
  const locale = localeProp || (currentLang === 'es' ? 'es_MX' : 'en_US');

  // Generate hreflang URLs
  const baseUrl = 'https://jamaklab.com';
  const currentPath = location.pathname;

  // Remove /es prefix if present to get the base path
  const basePath = currentPath.startsWith('/es')
    ? currentPath.replace(/^\/es/, '') || '/'
    : currentPath;

  const englishUrl = `${baseUrl}${basePath}`;
  const spanishUrl = `${baseUrl}/es${basePath === '/' ? '' : basePath}`;
  const siteTitle = title || siteConfig.siteTitle;
  const siteDescription = description || siteConfig.siteDescription;
  const siteKeywords = keywords || siteConfig.siteKeywords;
  const absoluteImage = (value) => new URL(value, baseUrl).href;
  const siteImage = absoluteImage(image || siteConfig.siteImage);
  const siteTwitterImage = absoluteImage(twitterImage || image || siteConfig.twitterImage || siteImage);
  const siteLinkedinImage = absoluteImage(linkedinImage || image || siteConfig.linkedinImage || siteImage);
  const siteFacebookImage = absoluteImage(facebookImage || image || siteConfig.facebookImage || siteImage);
  const imagePath = new URL(siteFacebookImage).pathname.toLowerCase();
  const imageType = imagePath.endsWith(".webp") ? "image/webp" : /\.jpe?g$/.test(imagePath) ? "image/jpeg" : "image/png";
  const siteUrl = url || (typeof window !== 'undefined' ? window.location.href : siteConfig.siteUrl);
  const canonical = canonicalUrl || siteUrl;

  const jsonLdData = structuredData ? JSON.stringify(structuredData) : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={siteDescription} />
      <meta name="keywords" content={siteKeywords} />
      <meta name="author" content={siteConfig.author} />
      <link rel="canonical" href={canonical} />
      
      {/* Robots Meta Tag */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:image" content={siteFacebookImage} />
      {!image && <meta property="og:image:width" content="1200" />}
      {!image && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={siteTitle} />
      <meta property="og:image:type" content={imageType} />
      <meta property="og:site_name" content={siteConfig.siteName} />
      <meta property="og:locale" content={locale} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={siteTwitterImage} />
      <meta name="twitter:image:alt" content={siteTitle} />

      {/* LinkedIn specific tags */}
      <meta property="og:image:secure_url" content={siteLinkedinImage} />
      
      {/* Article Meta Tags (for blog posts) */}
      {article && (
        <>
          <meta property="article:author" content={article.author} />
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:section" content={article.section} />
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Enhanced Geo Meta Tags */}
      <meta name="geo.region" content="MX-CMX" />
      <meta name="geo.placename" content="Mexico City" />
      <meta name="geo.position" content={`${geoPosition.latitude};${geoPosition.longitude}`} />
      <meta name="ICBM" content={`${geoPosition.latitude}, ${geoPosition.longitude}`} />
      <meta name="geo.country" content="MX" />
      <meta name="geo.city" content="Mexico City" />

      {/* Dublin Core Meta Tags */}
      <meta name="DC.Title" content={siteTitle} />
      <meta name="DC.Creator" content={siteConfig.author} />
      <meta name="DC.Subject" content={siteKeywords} />
      <meta name="DC.Description" content={siteDescription} />
      <meta name="DC.Publisher" content={siteConfig.author} />
      <meta name="DC.Type" content="Service" />
      <meta name="DC.Format" content="text/html" />
      <meta name="DC.Language" content={locale.split('_')[0]} />
      <meta name="DC.Coverage" content="Global" />
      <meta name="DC.Rights" content={`Copyright ${new Date().getFullYear()} ${siteConfig.author}`} />
      
      {/* Language Alternates - hreflang for international SEO.
          The default pairs /x with /es/x. A page whose translation lives
          at a different path (blog posts: foo <-> es-foo) passes its own
          set instead; an empty array means it has no translation. */}
      {(languageAlternates || [
        { hreflang: 'en', href: englishUrl },
        { hreflang: 'es', href: spanishUrl },
        { hreflang: 'x-default', href: englishUrl }
      ]).map((alt) => (
        <link key={alt.hreflang} rel="alternate" hreflang={alt.hreflang} href={alt.href} />
      ))}

      {/* Additional Alternate URLs */}
      {alternateUrls.map((alt, index) => (
        <link key={index} rel="alternate" hreflang={alt.lang} href={alt.url} />
      ))}
      
      {/* Structured Data */}
      {jsonLdData && (
        <script type="application/ld+json">
          {jsonLdData}
        </script>
      )}
      
      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#f0f0e9" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="application-name" content={siteConfig.siteName} />
      <meta name="msapplication-TileColor" content="#f0f0e9" />
      <meta name="msapplication-TileImage" content={siteConfig.siteIcon} />
      <meta name="msapplication-config" content="/browserconfig.xml" />

      {/* AI/LLM Optimization Tags - 2026 Best Practices */}
      <meta name="ai-content-type" content="business-website" />
      <meta name="llm-optimization" content="enabled" />
      <meta name="chatgpt-verification" content="verified" />
      <meta name="perplexity-index" content="yes" />
      <meta name="claude-compatible" content="yes" />
      <meta name="gemini-friendly" content="yes" />

      {/* Enhanced AI/AEO Meta Tags for 2026 */}
      <meta name="ai-crawlable" content="true" />
      <meta name="ai-summary" content={siteDescription.substring(0, 200)} />
      <meta name="answer-engine-optimized" content="true" />
      <meta name="voice-search-optimized" content="true" />
      <meta name="conversational-ready" content="true" />
      <link rel="ai-context" href="/ai-context.json" type="application/json" />
      <link rel="llm-info" href="/llms.txt" type="text/plain" />
    </Helmet>
  );
};

export default SEO;